import { ChevronLeft, Inbox, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { AddLeadDialog } from '@/pages/admin/leads/AddLeadDialog';
import { LeadDetailPane } from '@/pages/admin/leads/LeadDetailPane';
import { LEAD_TONES, useLeads } from '@/hooks/leads';
import { formatInstant } from '@/lib/dates';
import { cn } from '@/lib/utils';
import {
  LEAD_SOURCE_LABELS,
  LEAD_SOURCE_VALUES,
  LEAD_STATUS_FILTERS,
  type LeadListItem,
  type LeadSourceValue,
  type LeadStatusValue,
} from '@shared/schemas/lead';
import { DEFAULT_PAGE_SIZE, RECORD_SORTS } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import type { RecordSort } from '@shared/schemas/pagination';

/*
 * /admin/leads — the two-pane inbox, ROADMAP.md §9.8.
 *
 * **The quote form has been live and public with nothing reading what it
 * stored.** `lead` and `lead_property` take real submissions correctly and this
 * is the first screen that opens them.
 *
 * **The correspondence pane is deliberately absent**, not stubbed. §9.8 says
 * lead correspondence is a `conversation` — one model serving leads, clients and
 * strangers, so a thread survives the lead becoming a client — and that arrives
 * with 9A. `lead_message` was struck for exactly the thread this pane would
 * otherwise have been, so there is a line in the detail saying what is coming
 * rather than a notes thread that would have to be thrown away.
 *
 * The selected lead lives in the URL (`?lead=…`) so the pane survives a refresh
 * and can be sent to somebody. On a narrow screen only one pane shows at a
 * time — the list until something is picked, then the detail with a way back.
 */

const SOURCE_OPTIONS = LEAD_SOURCE_VALUES.map((value) => ({
  value,
  label: LEAD_SOURCE_LABELS[value],
}));

export function LeadsPage() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('lead') ?? '';

  const [status, setStatus] = useState<LeadStatusValue | 'all'>('new');
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<LeadSourceValue | ''>('');
  const [assignedTo, setAssignedTo] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<RecordSort>('newest');
  const [adding, setAdding] = useState(false);

  const leads = useLeads({ sort, page, status, search, source, assignedTo });
  const counts = leads.data?.counts;
  const filtered = search !== '' || source !== '' || assignedTo !== '';

  function select(id: string | null) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id === null) next.delete('lead');
        else next.set('lead', id);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <AdminPage
      title="Leads"
      description="Enquiries from the website and from the office, and what has been done about each one."
      actions={
        <Button
          onClick={() => {
            setAdding(true);
          }}
        >
          <UserPlus aria-hidden="true" />
          Record an enquiry
        </Button>
      }
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            label="Search leads"
            placeholder="Name, email, phone or town"
            className="sm:w-64"
          />

          <label className="flex items-center gap-2">
            <span className="sr-only">Filter by where the lead came from</span>
            <select
              name="source"
              value={source}
              onChange={(event) => {
                setSource(event.target.value as LeadSourceValue | '');
                setPage(1);
              }}
              className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body"
            >
              <option value="">Everywhere</option>
              {SOURCE_OPTIONS.map((entry) => (
                <option key={entry.value} value={entry.value}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2">
            <span className="sr-only">Filter by who it is assigned to</span>
            <select
              name="assignedTo"
              value={assignedTo}
              onChange={(event) => {
                setAssignedTo(event.target.value);
                setPage(1);
              }}
              className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body"
            >
              <option value="">Anyone</option>
              {/* Resolved against the signed-in admin on the server — a
                  browser-supplied "who I am" is a browser-supplied filter. */}
              <option value="me">Assigned to me</option>
              <option value="unassigned">Unassigned</option>
            </select>
          </label>
        </div>
      </div>

      <div className="mb-4">
        <FilterPills
          label="Filter leads by status"
          options={LEAD_STATUS_FILTERS.map((option) => ({
            ...option,
            ...(counts === undefined ? {} : { count: counts[option.value] }),
          }))}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        {/* The list. Hidden on a narrow screen once something is selected —
            two panes side by side at 375px is two unusable panes. */}
        <div className={cn('min-w-0', selectedId === '' ? '' : 'hidden lg:block')}>
          {/* The order control. A select rather than clickable headers —
              the list becomes cards below 768px and a card has no headers.
              Above the loading/error/list conditional, so it does not
              vanish on the two screens where somebody might reach for it. */}
          <div className="mb-3 flex justify-end">
            <SortSelect
              label="Sort leads"
              options={RECORD_SORTS}
              value={sort}
              onChange={(next) => {
                setSort(next);
                // Page 1, or you are on page 3 of a differently ordered list.
                setPage(1);
              }}
            />
          </div>

          {leads.error ? (
            /* A failed request is an error, never an empty state (P22). "No
               leads" under a broken query tells an office nobody enquired. */
            <div className="rounded-lg border border-ink-300 bg-white shadow-card">
              <ErrorState
                title="The leads inbox could not be loaded"
                onRetry={() => {
                  void leads.refetch();
                }}
              />
            </div>
          ) : leads.isPending ? (
            <div aria-busy="true">
              <span className="sr-only">Loading leads</span>
              <div aria-hidden="true" className="h-96 animate-pulse rounded-lg bg-ink-100" />
            </div>
          ) : leads.data && leads.data.data.length > 0 ? (
            <ul className="space-y-2">
              {leads.data.data.map((entry) => (
                <LeadRow
                  key={entry.id}
                  lead={entry}
                  selected={entry.id === selectedId}
                  onSelect={() => {
                    select(entry.id);
                  }}
                />
              ))}
            </ul>
          ) : (
            <div className="rounded-lg border border-ink-300 bg-white shadow-card">
              <EmptyState
                icon={Inbox}
                title={
                  filtered
                    ? 'No leads match those filters.'
                    : status === 'new'
                      ? 'Nothing new waiting on you.'
                      : 'No leads with that status.'
                }
                hint={
                  filtered
                    ? 'Try clearing the search or the filters.'
                    : status === 'new'
                      ? 'When somebody asks for a quote through the website, they land here.'
                      : 'Try another filter.'
                }
              />
            </div>
          )}

          {leads.data ? (
            <Pagination
              page={leads.data.page}
              pageSize={leads.data.pageSize || DEFAULT_PAGE_SIZE}
              total={leads.data.total}
              onPageChange={setPage}
              itemNoun="leads"
            />
          ) : null}
        </div>

        <div className={cn('min-w-0', selectedId === '' ? 'hidden lg:block' : '')}>
          {selectedId === '' ? (
            <div className="rounded-lg border border-ink-300 bg-white shadow-card">
              <EmptyState
                icon={Inbox}
                title="Pick a lead to see the enquiry."
                hint="A quote request carries the bedrooms, bathrooms and square footage for every property they described."
              />
            </div>
          ) : (
            <>
              <div className="mb-3 lg:hidden">
                <Button
                  variant="ghost"
                  onClick={() => {
                    select(null);
                  }}
                >
                  <ChevronLeft aria-hidden="true" />
                  All leads
                </Button>
              </div>
              <LeadDetailPane
                leadId={selectedId}
                onClose={() => {
                  select(null);
                }}
              />
            </>
          )}
        </div>
      </div>
      {/*
        **Opens the new lead rather than only closing.** Somebody who has just
        written down a phone call is about to do something with it — set a
        status, add a note, convert it — and landing them back on an unchanged
        list means finding the row they just made.
      */}
      <AddLeadDialog
        open={adding}
        onOpenChange={setAdding}
        onCreated={(created) => {
          select(created.id);
        }}
      />
    </AdminPage>
  );
}

function LeadRow({
  lead,
  selected,
  onSelect,
}: {
  lead: LeadListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        aria-current={selected ? 'true' : undefined}
        onClick={onSelect}
        className={cn(
          'w-full rounded-lg border p-3 text-left shadow-card',
          selected ? 'border-bark-700 bg-honey-50' : 'border-ink-300 bg-white hover:bg-honey-50/60',
        )}
      >
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-body font-medium text-ink-900">
            {lead.businessName ?? lead.displayName}
          </span>
          <StatusBadge label={lead.statusLabel} tone={LEAD_TONES[lead.status]} />
        </span>

        <span className="mt-0.5 block text-meta text-ink-500">
          {/* Humanised, never `quote_request` (P5). */}
          {lead.sourceLabel} · {formatInstant(lead.createdAt)}
        </span>

        <span className="mt-1 block text-meta text-ink-700">
          {lead.propertyCount === 0
            ? 'No properties described'
            : /*
               * **The count is the point.** A twelve-property enquiry from a
               * property manager and a one-bedroom flat are not the same job,
               * and without this block they look identical in a list.
               */
              `${String(lead.propertyCount)} ${lead.propertyCount === 1 ? 'property' : 'properties'}`}
          {lead.city ? ` · ${lead.city}` : ''}
          {lead.assignedToName ? ` · ${lead.assignedToName}` : ''}
        </span>
      </button>
    </li>
  );
}
